import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Crown, X, Fire, EnvelopeSimple, CheckCircle, Timer } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { getPublicStats } from "../lib/publicStats";
import { useAuth } from "../lib/auth";

/**
 * Exit-intent retention modal.
 *
 * Triggers:
 *  - Desktop: cursor leaves viewport from the top (clientY <= 0)
 *  - Mobile: rapid upward scroll (+ at least 25% page scrolled)
 *
 * Conditions:
 *  - Hidden on conversion/flow pages (/passez-partenaire, /dashboard, /admin, /connexion, /auth/*)
 *  - Hidden when user is authenticated AND owns ≥ 1 business
 *  - Hidden if dismissed within last 7 days (localStorage)
 *  - Countdown = 24 h from first dismissal or page load (persists in localStorage)
 */
const STORAGE_DISMISSED = "mc68_exit_intent_dismissed_at";
const STORAGE_EXPIRY = "mc68_exit_intent_expiry";
const DISMISS_DAYS = 7;
const LIMIT = 100;
const EXCLUDED_PREFIXES = [
  "/passez-partenaire",
  "/dashboard",
  "/admin",
  "/connexion",
  "/auth/",
];

function timeLeft(expiry) {
  const diff = expiry - Date.now();
  if (diff <= 0) return { h: 0, m: 0, s: 0 };
  return {
    h: Math.floor(diff / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("idle"); // idle | capture | sent
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [countdown, setCountdown] = useState({ h: 24, m: 0, s: 0 });
  const expiryRef = useRef(null);
  const lastScrollY = useRef(0);
  const triggered = useRef(false);
  const { user } = useAuth();
  const { pathname, search } = useLocation();

  // Fetch scarcity once
  useEffect(() => {
    getPublicStats().then((r) => {
      const taken = r.data?.premium_businesses ?? 0;
      setRemaining(Math.max(0, LIMIT - taken));
    }).catch(() => {});
  }, []);

  // If the user is authenticated, check their business ownership
  const [hasBusiness, setHasBusiness] = useState(false);
  useEffect(() => {
    if (!user) { setHasBusiness(false); return; }
    api.get("/dashboard/businesses").then((r) => {
      setHasBusiness((r.data || []).length > 0);
    }).catch(() => setHasBusiness(false));
  }, [user]);

  // Expiry countdown
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_EXPIRY);
    let expiry;
    if (stored && Number(stored) > Date.now()) {
      expiry = Number(stored);
    } else {
      expiry = Date.now() + 24 * 3600 * 1000;
      localStorage.setItem(STORAGE_EXPIRY, String(expiry));
    }
    expiryRef.current = expiry;
    setCountdown(timeLeft(expiry));
    const itv = setInterval(() => setCountdown(timeLeft(expiryRef.current)), 1000);
    return () => clearInterval(itv);
  }, []);

  const shouldDisplay = () => {
    if (!remaining || remaining === 0) return false;
    if (hasBusiness) return false;
    if (EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p))) return false;
    const dismissed = localStorage.getItem(STORAGE_DISMISSED);
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400 * 1000) return false;
    // Cookie banner must be decided first (avoid stacking)
    if (!localStorage.getItem("mc68_cookies")) return false;
    return true;
  };

  // Exit intent detection
  useEffect(() => {
    if (triggered.current) return;
    const onMouseOut = (e) => {
      if (triggered.current) return;
      if (!shouldDisplay()) return;
      // Desktop: cursor leaves viewport from top
      if (e.clientY <= 0 && !e.relatedTarget) {
        triggered.current = true;
        setOpen(true);
      }
    };
    const onScroll = () => {
      if (triggered.current) return;
      if (!shouldDisplay()) return;
      const y = window.scrollY;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = h > 0 ? y / h : 0;
      // Mobile upward scroll after consuming some of the page
      const isMobile = window.innerWidth < 768;
      if (isMobile && scrolled > 0.25 && y < lastScrollY.current - 40) {
        triggered.current = true;
        setOpen(true);
      }
      lastScrollY.current = y;
    };
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, remaining, hasBusiness]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_DISMISSED, String(Date.now()));
    setOpen(false);
  };

  const sendMagic = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error("Email invalide");
      return;
    }
    setSending(true);
    try {
      const onBusiness = pathname.startsWith("/entreprise/");
      const redirect = onBusiness
        ? `${pathname}${search}${search.includes("claim=") ? "" : (search ? "&" : "?") + "claim=1"}`
        : "/passez-partenaire";
      await api.post("/auth/magic-link", {
        email: trimmed,
        language: "fr",
        redirect,
      });
      setMode("sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi");
    }
    setSending(false);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] bg-foreground/40 backdrop-blur-sm grid place-items-center p-4"
          onClick={dismiss}
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="relative overflow-hidden rounded-sm border-2 border-secondary bg-card max-w-md w-full shadow-[0_40px_120px_-20px_rgba(0,0,0,0.45)]"
            data-testid="exit-intent-modal"
          >
            <div className="absolute inset-0 opacity-60 pointer-events-none" style={{ background: "radial-gradient(140% 90% at 0% 0%, rgba(200,121,90,0.28), transparent 55%), radial-gradient(120% 80% at 100% 100%, rgba(19,78,52,0.16), transparent 55%)" }} />
            <button
              onClick={dismiss}
              aria-label="Fermer"
              className="absolute top-3 right-3 z-10 w-8 h-8 grid place-items-center text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-sm transition-colors"
              data-testid="exit-intent-close"
            >
              <X size={16} weight="bold" />
            </button>

            <div className="relative p-7 sm:p-8">
              {mode !== "sent" && (
                <>
                  <div className="inline-flex items-center gap-1.5 bg-secondary/15 text-secondary text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-sm mb-4">
                    <Fire size={12} weight="fill" /> Offre lancement · expire bientôt
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl leading-tight font-medium tracking-tight mb-3">
                    Attendez — votre remise <span className="text-gradient">−50 %</span> est encore active.
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    Il reste <b className="text-foreground">{remaining}</b> places parmi les 100 premiers commerçants partenaires du Haut-Rhin. Ensuite, tarif plein. Capturez votre fiche maintenant.
                  </p>

                  <div className="bg-accent/40 border border-border rounded-sm p-3 mb-5 flex items-center gap-3" data-testid="exit-intent-countdown">
                    <Timer size={20} className="text-secondary shrink-0" weight="duotone" />
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Expire dans</div>
                      <div className="font-display text-lg tabular-nums font-medium">
                        {String(countdown.h).padStart(2, "0")}h {String(countdown.m).padStart(2, "0")}m {String(countdown.s).padStart(2, "0")}s
                      </div>
                    </div>
                  </div>
                </>
              )}

              {mode === "idle" && (
                <div className="flex flex-col gap-2">
                  {user ? (
                    <Link
                      to="/passez-partenaire"
                      onClick={dismiss}
                      className="inline-flex items-center justify-center gap-2 w-full bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-5 py-3 text-sm font-semibold rounded-sm transition-all"
                      data-testid="exit-intent-cta-primary"
                    >
                      <Crown size={14} weight="fill" /> Activer mon offre −50 %
                    </Link>
                  ) : (
                    <button
                      onClick={() => setMode("capture")}
                      className="inline-flex items-center justify-center gap-2 w-full bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-5 py-3 text-sm font-semibold rounded-sm transition-all hover:shadow-[0_16px_36px_-14px_rgba(200,121,90,0.6)]"
                      data-testid="exit-intent-cta-primary"
                    >
                      <Crown size={14} weight="fill" /> Garder ma remise −50 %
                    </button>
                  )}
                  <button
                    onClick={dismiss}
                    className="w-full text-[11px] text-muted-foreground hover:text-foreground"
                    data-testid="exit-intent-cta-secondary"
                  >
                    Non merci, je préfère payer le prix fort plus tard.
                  </button>
                </div>
              )}

              {mode === "capture" && (
                <form onSubmit={sendMagic} className="space-y-3" data-testid="exit-intent-capture-form">
                  <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Votre email pro</label>
                  <div className="flex items-center gap-0 border border-secondary/60 rounded-sm overflow-hidden focus-within:border-secondary">
                    <EnvelopeSimple size={16} className="ml-3 text-secondary" />
                    <input
                      autoFocus
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.fr"
                      className="flex-1 bg-transparent px-2 py-3 text-sm outline-none"
                      data-testid="exit-intent-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-5 py-3 text-sm font-semibold rounded-sm disabled:opacity-60"
                    data-testid="exit-intent-send"
                  >
                    {sending ? "Envoi…" : "Je verrouille ma remise −50 %"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("idle")}
                    className="w-full text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    ← Retour
                  </button>
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    Lien magique sans mot de passe · valable 30 min · aucune carte demandée.
                  </p>
                </form>
              )}

              {mode === "sent" && (
                <div className="text-center py-4" data-testid="exit-intent-sent">
                  <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mb-4">
                    <CheckCircle size={36} weight="fill" />
                  </div>
                  <h3 className="font-display text-xl font-medium mb-2">Remise verrouillée !</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    Ouvrez votre boîte <b>{email}</b>, cliquez sur le lien sécurisé, et vous serez directement conduit à l'activation de votre offre −50 %.
                  </p>
                  <button
                    onClick={dismiss}
                    className="text-xs font-semibold text-primary hover:text-secondary"
                    data-testid="exit-intent-close-success"
                  >
                    Fermer
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
